import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowRight, 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  Trophy,
  XCircle,
  Clock3,
  Search,
  RefreshCw,
  CheckCircle2,
  Ban,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminCancellationsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cancellations, setCancellations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [fees, setFees] = useState([]);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [feeFilter, setFeeFilter] = useState('unpaid');
  const [settlingId, setSettlingId] = useState(null);

  useEffect(() => {
    // Wait for user to be loaded before checking admin status
    if (user === null) {
      // User is still loading, wait
      return;
    }
    if (user === undefined || !user?.is_admin) {
      navigate('/');
      return;
    }
    fetchCancellations();
    fetchStats();
    fetchFees();
  }, [user, navigate]);

  const fetchFees = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/cancellation-fees?status=${feeFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFees(response.data.fees || []);
      setTotalUnpaid(response.data.total_unpaid || 0);
    } catch (error) {
      console.error('Error fetching fees:', error);
    }
  };

  useEffect(() => {
    if (user?.is_admin) fetchFees();
  }, [feeFilter]); // eslint-disable-line

  const settleFee = async (recordId, action) => {
    setSettlingId(recordId);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/admin/cancellation-fees/${recordId}/settle`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'waived' ? 'דמי הביטול בוטלו (מחילה)' : 'סומן כשולם');
      if (response.data.user_unsuspended) {
        toast.success('השחקן שוחרר מהשהיה ויכול להירשם שוב', { duration: 5000 });
      }
      fetchFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהסדרת דמי הביטול');
    } finally {
      setSettlingId(null);
    }
  };

  const fetchCancellations = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/cancellations?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancellations(response.data.cancellations);
    } catch (error) {
      toast.error('שגיאה בטעינת ביטולים');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/cancellations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'לא זמין';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredCancellations = cancellations.filter(c => 
    c.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.tournament_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowRight className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">ביטולי הרשמות</h1>
              <p className="text-gray-500 text-sm">מעקב אחר שחקנים שביטלו הרשמה לתחרויות</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => { fetchCancellations(); fetchStats(); fetchFees(); }}>
            <RefreshCw className="w-4 h-4 ml-2" />
            רענן
          </Button>
        </div>

        {/* Cancellation Fees Management */}
        <Card className="mb-6 border-amber-300" data-testid="cancellation-fees-section">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-amber-600" />
                דמי ביטול ({fees.length})
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="text-sm bg-red-50 text-red-700 font-bold px-3 py-1.5 rounded-lg" data-testid="total-unpaid-fees">
                  סה"כ חוב פתוח: {totalUnpaid}₪
                </div>
                <div className="flex rounded-lg overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setFeeFilter('unpaid')}
                    data-testid="fee-filter-unpaid"
                    className={`px-3 py-1.5 text-sm ${feeFilter === 'unpaid' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'}`}
                  >
                    לא שולמו
                  </button>
                  <button
                    onClick={() => setFeeFilter('all')}
                    data-testid="fee-filter-all"
                    className={`px-3 py-1.5 text-sm ${feeFilter === 'all' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'}`}
                  >
                    הכל
                  </button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {fees.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p>אין דמי ביטול {feeFilter === 'unpaid' ? 'פתוחים' : 'להצגה'} 🎉</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fees.map((fee, idx) => (
                  <div
                    key={fee.id || idx}
                    data-testid={`fee-row-${idx}`}
                    className="border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-[220px]">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800" data-testid={`fee-player-name-${idx}`}>{fee.user_name}</p>
                        <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                          {fee.user_phone && (
                            <a href={`tel:${fee.user_phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                              <Phone className="w-3 h-3" />{fee.user_phone}
                            </a>
                          )}
                          <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />{fee.tournament_name}</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <span className="text-xl font-extrabold text-red-600" data-testid={`fee-amount-${idx}`}>{fee.fee_amount}₪</span>
                      {fee.hours_before_start != null && (
                        <p className="text-xs text-gray-500">בוטל {fee.hours_before_start} ש' לפני</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                      {fee.fee_status === 'unpaid' ? (
                        <>
                          <Button
                            size="sm"
                            disabled={settlingId === fee.id}
                            onClick={() => settleFee(fee.id, 'paid')}
                            data-testid={`settle-paid-btn-${idx}`}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <CheckCircle2 className="w-4 h-4 ml-1" />
                            סומן כשולם
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={settlingId === fee.id}
                            onClick={() => settleFee(fee.id, 'waived')}
                            data-testid={`settle-waived-btn-${idx}`}
                          >
                            <Ban className="w-4 h-4 ml-1" />
                            מחילה
                          </Button>
                        </>
                      ) : (
                        <span
                          className={`px-3 py-1 text-xs rounded-full font-medium ${fee.fee_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
                          data-testid={`fee-status-badge-${idx}`}
                        >
                          {fee.fee_status === 'paid' ? '✓ שולם' : '○ מחילה'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-sm opacity-90">סה"כ ביטולים</p>
                    <p className="text-2xl font-bold">{stats.total_cancellations}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-sm opacity-90">מרשומים</p>
                    <p className="text-2xl font-bold">{stats.from_registered}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock3 className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-sm opacity-90">מרשימת המתנה</p>
                    <p className="text-2xl font-bold">{stats.from_waiting_list}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-sm opacity-90">7 ימים אחרונים</p>
                    <p className="text-2xl font-bold">{stats.last_7_days}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="חיפוש לפי שם שחקן, תחרות או אימייל..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
          </div>
        </div>

        {/* Cancellations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              רשימת ביטולים ({filteredCancellations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredCancellations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <XCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>אין ביטולים להצגה</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCancellations.map((cancellation, idx) => (
                  <div
                    key={cancellation.id || idx}
                    data-testid={`cancellation-row-${idx}`}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      {/* Player Info */}
                      <div className="flex items-center gap-3 min-w-[220px]">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800" data-testid={`cancel-player-name-${idx}`}>{cancellation.user_name}</p>
                          <div className="flex flex-col gap-1 text-xs text-gray-500">
                            {cancellation.user_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {cancellation.user_email}
                              </span>
                            )}
                            {cancellation.user_phone && (
                              <a href={`tel:${cancellation.user_phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                <Phone className="w-3 h-3" />
                                {cancellation.user_phone}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Tournament Info */}
                      <div className="flex flex-col gap-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-blue-500" />
                          <span className="font-medium">{cancellation.tournament_name}</span>
                        </div>
                        {cancellation.tournament_date && (
                          <span className="text-xs text-gray-500 mr-6">
                            תאריך תחרות: {formatDate(cancellation.tournament_date)}
                          </span>
                        )}
                      </div>

                      {/* Cancellation Time */}
                      <div className="text-left">
                        <div className="flex items-center gap-2 text-sm text-gray-600 justify-end">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(cancellation.cancelled_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 justify-end">
                          <Clock className="w-4 h-4" />
                          <span>{formatTime(cancellation.cancelled_at)}</span>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex flex-col gap-1 items-end">
                        {cancellation.was_registered ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full whitespace-nowrap">
                            ביטל מהרשומים
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full whitespace-nowrap">
                            ביטל מרשימת המתנה
                          </span>
                        )}
                        {cancellation.removed_by_admin && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full whitespace-nowrap" title={cancellation.admin_name ? `ע"י ${cancellation.admin_name}` : ''}>
                            הוסר ע"י אדמין
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCancellationsPage;
